import { useEffect, useMemo, useRef } from 'react';
import { captureSiteArea } from './site-area-capture';
import { previewLinks } from './site-preview-links';

export type SiteSelection = {
  tag: string;
  text: string;
  selector: string;
  html: string;
  screenshot?: string;
  region?: {
    x: number;
    y: number;
    width: number;
    height: number;
    viewportWidth: number;
    viewportHeight: number;
  };
};
const selector = 'h1,h2,h3,h4,p,a,button,img,li,section,header,footer';

export function siteElementSelector(node: Element, body: HTMLElement): string {
  const parts: string[] = [];
  let current: Element | null = node;
  while (current && current !== body) {
    const tag = current.tagName.toLowerCase();
    const siblings: Element[] = current.parentElement
      ? Array.from(current.parentElement.children).filter(
          (item) => item.tagName === current?.tagName
        )
      : [];
    parts.unshift(`${tag}:nth-of-type(${siblings.indexOf(current) + 1})`);
    current = current.parentElement;
  }
  return `body > ${parts.join(' > ')}`;
}

// Only this editor-owned bridge can execute. Generated scripts/event handlers are
// removed, the CSP permits a fresh nonce, and the iframe retains an opaque origin.
export function selectablePreview(html: string, token: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Keep only the fixed API-owned catalogue loader in capture mode.
  const liveRuntime = doc
    .querySelector('script#claire-live-data[data-catalog-url]')
    ?.cloneNode(true) as HTMLScriptElement | undefined;
  for (const node of doc.querySelectorAll('script,meta[http-equiv]'))
    node.remove();
  for (const node of doc.querySelectorAll('*')) {
    for (const attr of Array.from(node.attributes)) {
      if (
        attr.name.startsWith('on') ||
        attr.name === 'nonce' ||
        attr.name.startsWith('data-editor-')
      )
        node.removeAttribute(attr.name);
    }
  }
  const targets: SiteSelection[] = [];
  for (const [index, node] of Array.from(doc.body.querySelectorAll(selector))
    .slice(0, 2000)
    .entries()) {
    targets.push({
      tag: node.tagName.toLowerCase(),
      text: (node.getAttribute('alt') || node.textContent || '')
        .trim()
        .slice(0, 250),
      selector: siteElementSelector(node, doc.body),
      html: node.outerHTML.slice(0, 1200),
    });
    node.setAttribute('data-editor-target', String(index));
  }
  const policy = doc.createElement('meta');
  policy.httpEquiv = 'Content-Security-Policy';
  policy.content = `default-src 'none'; script-src 'nonce-${token}'; style-src 'unsafe-inline' https://fonts.googleapis.com; img-src https: data:; font-src https: data:; base-uri 'none'; form-action 'none'; frame-src 'none'; connect-src 'none'`;
  if (liveRuntime) {
    const endpoint = new URL(liveRuntime.getAttribute('data-catalog-url') || '')
      .href;
    policy.content = policy.content.replace(
      "connect-src 'none'",
      `connect-src ${endpoint}`
    );
    liveRuntime.setAttribute('nonce', token);
    doc.body.append(liveRuntime);
  }
  doc.head.prepend(policy);
  const style = doc.createElement('style');
  style.textContent =
    '[data-editor-overlay]{position:fixed!important;margin:0!important;padding:0!important;border:0!important;border-radius:0!important;transform:none!important;opacity:1!important;filter:none!important;box-sizing:border-box!important;background:transparent!important;box-shadow:none!important;pointer-events:auto!important;cursor:crosshair!important;user-select:none!important;-webkit-user-select:none!important;touch-action:none!important} [data-editor-overlay]:hover,[data-editor-overlay]:focus-visible{outline:2px solid #8b5cf6!important;outline-offset:2px} [data-editor-selected]{outline:3px solid #8b5cf6!important;outline-offset:2px}';
  doc.head.append(style);
  const script = doc.createElement('script');
  script.setAttribute('nonce', token);
  script.textContent = `
(function(){
 var token=${JSON.stringify(token)};
 var nodes=Array.from(document.querySelectorAll('[data-editor-target]'));
 var layer=document.createElement('button');layer.type='button';
 layer.setAttribute('aria-label','Drag to capture website area. Press Enter to capture the visible page, Escape to cancel.');
 layer.setAttribute('data-editor-capture','');layer.setAttribute('data-editor-overlay','');
 layer.style.cssText='position:fixed;inset:0;width:100%;height:100%;border:0;margin:0;padding:0;background:transparent;cursor:crosshair;z-index:2147483647;touch-action:none';
 var box=document.createElement('div');box.setAttribute('data-editor-capture','');
 box.style.cssText='position:fixed;display:none;border:2px solid #8b5cf6;background:rgba(139,92,246,.12);box-shadow:0 0 0 9999px rgba(0,0,0,.18);pointer-events:none;z-index:2147483647';
 document.body.append(layer,box);layer.focus({preventScroll:true});var start=null,busy=false,pointerId=null;
 function rectangle(x,y){var left=Math.min(start.x,x),top=Math.min(start.y,y);return {left:left,top:top,width:Math.abs(x-start.x),height:Math.abs(y-start.y)}}
 function point(e){return {x:Math.max(0,Math.min(innerWidth,e.clientX)),y:Math.max(0,Math.min(innerHeight,e.clientY))}}
 function begin(e){if(busy||start||e.button!==0||e.isPrimary===false)return;start=point(e);pointerId=e.pointerId;layer.focus({preventScroll:true});e.preventDefault();if(pointerId!==undefined){try{layer.setPointerCapture(pointerId)}catch(_){/* Document release listeners still finish the drag. */}}}
 function move(e){if(!start||(e.pointerId!==undefined&&e.pointerId!==pointerId))return;var p=point(e),r=rectangle(p.x,p.y);box.style.display='block';box.style.left=r.left+'px';box.style.top=r.top+'px';box.style.width=r.width+'px';box.style.height=r.height+'px'}
 function cancel(){start=null;pointerId=null;box.style.display='none'}
 function finish(e){if(e.button!==0||!start||(e.pointerId!==undefined&&e.pointerId!==pointerId))return;var p=point(e),r=rectangle(p.x,p.y);cancel();if(r.width<8||r.height<8)return;capture(r)}
 layer.addEventListener('pointerdown',begin);
 layer.addEventListener('mousedown',begin);
 // Listen beyond the layer and support mouse releases when pointer capture is lost.
 // Capture-phase document listeners finish once; compatibility events see no start.
 document.addEventListener('pointermove',move,true);
 document.addEventListener('mousemove',move,true);
 document.addEventListener('pointerup',finish,true);
 document.addEventListener('mouseup',finish,true);
 document.addEventListener('pointercancel',function(e){if(e.pointerId===pointerId)cancel()},true);
 layer.addEventListener('dragstart',function(e){e.preventDefault()});
 layer.addEventListener('keydown',function(e){if(e.key==='Escape'){cancel();parent.postMessage({type:'site-capture-cancel',token:token},'*')}if(e.key==='Enter'){e.preventDefault();cancel();capture({left:0,top:0,width:innerWidth,height:innerHeight})}});
 async function capture(r){if(busy)return;busy=true;layer.style.cursor='wait';
 var ids=nodes.map(function(n,i){var b=n.getBoundingClientRect();return b.width&&b.height&&b.left<r.left+r.width&&b.right>r.left&&b.top<r.top+r.height&&b.bottom>r.top?i:-1}).filter(function(i){return i>=0}).slice(0,40);
 var region={x:r.left+scrollX,y:r.top+scrollY,width:r.width,height:r.height,viewportWidth:innerWidth,viewportHeight:innerHeight};
 var snapshot=document.documentElement.cloneNode(true);snapshot.querySelectorAll('script,[data-editor-capture]').forEach(function(n){n.remove()});
 var snapshotHtml='<!doctype html>'+snapshot.outerHTML;
 var text=nodes.filter(function(n){var b=n.getBoundingClientRect();return b.width&&b.height&&b.left<r.left+r.width&&b.right>r.left&&b.top<r.top+r.height&&b.bottom>r.top}).map(function(n){return n.getAttribute('alt')||n.textContent||''}).join(' · ').slice(0,250);
 parent.postMessage({type:'site-region',token:token,ids:ids,region:region,text:text,snapshotHtml:snapshotHtml.length<=512000?snapshotHtml:undefined},'*');
 busy=false;layer.style.cursor='crosshair';box.style.display='none';}
 document.addEventListener('click',function(e){e.preventDefault()},true);
})();`;
  doc.body.append(script);
  return { html: `<!doctype html>${doc.documentElement.outerHTML}`, targets };
}

export function SitePreview({
  html,
  device,
  selecting,
  onSelect,
  onCancel,
}: {
  html: string;
  device: 'desktop' | 'tablet' | 'mobile';
  selecting: boolean;
  onSelect: (target: SiteSelection) => void;
  onCancel?: () => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const capturing = useRef(false);
  const browseHtml = useMemo(() => previewLinks(html), [html]);
  const prepared = useMemo(
    () =>
      selecting
        ? (() => {
            const token = crypto.randomUUID();
            return { ...selectablePreview(html, token), token };
          })()
        : null,
    [html, selecting]
  );
  useEffect(() => {
    if (!prepared) return;
    const active = prepared;
    let current = true;
    function receive(event: MessageEvent) {
      if (
        event.source !== frame.current?.contentWindow ||
        event.origin !== 'null' ||
        event.data?.token !== active.token
      )
        return;
      if (event.data.type === 'site-capture-cancel') {
        onCancel?.();
        return;
      }
      if (
        event.data.type === 'site-element' &&
        Number.isInteger(event.data.id)
      ) {
        const target = active.targets[event.data.id];
        if (target) onSelect(target);
        return;
      }
      if (event.data.type !== 'site-region' || !Array.isArray(event.data.ids))
        return;
      const region = event.data.region;
      if (
        !region ||
        !['x', 'y', 'width', 'height', 'viewportWidth', 'viewportHeight'].every(
          (key) => Number.isFinite(region[key]) && region[key] >= 0
        ) ||
        region.width < 8 ||
        region.height < 8 ||
        region.width > region.viewportWidth ||
        region.height > region.viewportHeight
      )
        return;
      const targets = event.data.ids
        .slice(0, 40)
        .filter((id: unknown) => Number.isInteger(id))
        .map((id: number) => active.targets[id])
        .filter(Boolean);
      if (capturing.current) return;
      capturing.current = true;
      const target: SiteSelection = {
        tag: 'region',
        text:
          typeof event.data.text === 'string'
            ? event.data.text.slice(0, 250)
            : targets
                .map((target: SiteSelection) => target.text)
                .join(' · ')
                .slice(0, 250) || 'Captured area',
        selector: targets
          .map((target: SiteSelection) => target.selector)
          .join(', ')
          .slice(0, 1200),
        html: targets
          .map((target: SiteSelection) => target.html)
          .join('\n')
          .slice(0, 2400),
        region,
      };
      const snapshot =
        typeof event.data.snapshotHtml === 'string' &&
        event.data.snapshotHtml.length <= 512000
          ? event.data.snapshotHtml
          : html;
      void captureSiteArea(snapshot, region)
        .then((screenshot) => {
          if (current) onSelect({ ...target, screenshot });
        })
        .catch(() => {
          if (current) onSelect(target);
        })
        .finally(() => {
          capturing.current = false;
        });
    }
    window.addEventListener('message', receive);
    return () => {
      current = false;
      window.removeEventListener('message', receive);
    };
  }, [prepared, onSelect, onCancel, html]);
  return (
    <iframe
      key={selecting ? 'select' : 'browse'}
      ref={frame}
      title="Public website preview"
      srcDoc={prepared?.html || browseHtml}
      sandbox={`allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation${selecting || html.includes('<meta name="claire-design" content="2">') || html.includes('<script id="claire-live-data"') ? ' allow-scripts' : ''}`}
      referrerPolicy="no-referrer"
      className={`site-frame min-h-[640px] max-w-full rounded-xl border border-stone-200 bg-white shadow-xl shadow-stone-900/5 ${device === 'mobile' ? 'w-[390px]' : device === 'tablet' ? 'w-[768px]' : 'w-full'}`}
    />
  );
}
